import { create } from 'zustand';

/**
 * The offline app shell and its updates (docs/08 "A service worker update must not
 * reload an unsaved session", docs/15 "Offline updates", REQ-OFFLINE-01, ADR 0013).
 *
 * The service worker (src/serviceWorker/sw.ts) keeps a copy of the app on the device. A
 * new version installs in the background and then waits. This page shows “A new version
 * is ready”; only when the person chooses to update does it ask every open workspace
 * whether its work is safe (the studio saves first and refuses while a line is being
 * drawn or a save is failing), and only then does the new version take over and the
 * page reload. A page never reloads by itself once it is in use; the one exception is
 * the moment the app starts, when a version that finished installing on an earlier visit
 * takes over before anything has been done.
 */
export type ShellStatus =
  /** Development server or tests: no service worker is built. */
  | 'disabled'
  /** The browser has no service workers. */
  | 'unsupported'
  /** Plain http:// on a network address: browsers allow service workers only on HTTPS. */
  | 'insecure'
  | 'installing'
  /** A copy of the app is stored on this device and opens without a connection. */
  | 'ready'
  | 'error';

export interface OfflineShellState {
  readonly status: ShellStatus;
  readonly detail: string | null;
  /** A new version is installed and waiting for the person's choice. */
  readonly updateWaiting: boolean;
  /** Another tab switched to a newer version; this tab still runs the older one. */
  readonly updatedElsewhere: boolean;
  readonly applying: boolean;
  /** Why the update is waiting (for example unsaved work), shown next to the button. */
  readonly problem: string | null;
  /** “Later” hides the prompt until the next version is found. */
  readonly dismissed: boolean;
}

export const useOfflineShell = create<OfflineShellState>(() => ({
  status: 'disabled',
  detail: null,
  updateWaiting: false,
  updatedElsewhere: false,
  applying: false,
  problem: null,
  dismissed: false,
}));

export type UpdateGuardResult =
  { readonly ok: true } | { readonly ok: false; readonly reason: string };
/** Asked before the app updates; the studio saves its work here. */
export type UpdateGuard = () => Promise<UpdateGuardResult>;

/** The parts of the browser the shell needs (tests pass fakes). */
export interface ShellEnvironment {
  readonly enabled: boolean;
  readonly container: ServiceWorkerContainer | undefined;
  readonly secure: boolean;
  readonly scriptUrl: string;
  readonly scope: string;
  readonly reload: () => void;
  /** Re-checks for a new version when the page becomes visible and every hour. */
  readonly scheduleChecks?: (check: () => void) => void;
  readonly setTimeout?: (fn: () => void, ms: number) => void;
}

const guards = new Set<UpdateGuard>();
let registration: ServiceWorkerRegistration | null = null;
let environment: ShellEnvironment | null = null;
let userChoseUpdate = false;

export function registerUpdateGuard(guard: UpdateGuard): () => void {
  guards.add(guard);
  return () => guards.delete(guard);
}

const set = (patch: Partial<OfflineShellState>) => useOfflineShell.setState(patch);

export async function startOfflineShell(env: ShellEnvironment): Promise<void> {
  environment = env;
  if (!env.enabled) {
    set({ status: 'disabled', detail: null });
    return;
  }
  const container = env.container;
  if (!container) {
    set({ status: 'unsupported', detail: null });
    return;
  }
  if (!env.secure) {
    set({ status: 'insecure', detail: null });
    return;
  }
  let controlled = container.controller !== null;
  container.addEventListener('controllerchange', () => {
    if (userChoseUpdate) {
      env.reload();
      return;
    }
    if (controlled) {
      // Another tab chose to update. This page keeps working with the code it has.
      set({ updatedElsewhere: true, updateWaiting: false, dismissed: false });
    } else {
      controlled = true; // the first install took over this page
    }
  });

  set({ status: 'installing', detail: null });
  let reg: ServiceWorkerRegistration;
  try {
    reg = await container.register(env.scriptUrl, { scope: env.scope, updateViaCache: 'none' });
  } catch (error) {
    set({
      status: 'error',
      detail:
        error instanceof Error && error.message
          ? error.message
          : 'The browser did not allow the app to be saved for offline use.',
    });
    return;
  }
  // Only a test harness that blocks service workers (Playwright's WebKit) resolves without a
  // registration; say so instead of failing later on a missing object.
  if (!(reg as ServiceWorkerRegistration | undefined)) {
    set({
      status: 'error',
      detail: 'The browser did not allow the app to be saved for offline use.',
    });
    return;
  }
  registration = reg;

  // True while a version installed on an earlier visit takes over at start (no prompt).
  let swappingAtStart = false;
  const watch = (worker: ServiceWorker | null) => {
    if (!worker) return;
    const onState = () => {
      if (worker.state === 'installed' && container.controller) {
        if (!swappingAtStart) set({ updateWaiting: true, dismissed: false });
      } else if (worker.state === 'activated') {
        set({ status: 'ready', detail: null });
      } else if (worker.state === 'redundant' && !reg.active) {
        set({
          status: 'error',
          detail:
            'The app could not be saved for offline use. It will try again on the next visit.',
        });
      }
    };
    worker.addEventListener('statechange', onState);
    onState();
  };

  if (reg.active) set({ status: 'ready', detail: null });
  if (reg.waiting && container.controller) {
    // A version that finished installing on an earlier visit takes over as the app starts:
    // nothing has been done in this page yet, so there is no work to protect (ADR 0013).
    // Phones keep an app's page open for days, and a tap on “Update now” is easy to miss.
    // Should the swap not happen, the page keeps working and offers the update as usual.
    swappingAtStart = true;
    userChoseUpdate = true;
    reg.waiting.postMessage({ type: 'SKIP_WAITING' });
    (env.setTimeout ?? ((fn, ms) => window.setTimeout(fn, ms)))(() => {
      if (!swappingAtStart) return;
      swappingAtStart = false;
      userChoseUpdate = false;
      if (reg.waiting) set({ updateWaiting: true, dismissed: false });
    }, 5_000);
  }
  watch(reg.installing);
  watch(reg.waiting);
  reg.addEventListener('updatefound', () => watch(reg.installing));
  env.scheduleChecks?.(() => {
    void reg.update().catch(() => undefined); // offline or server unreachable: try later
  });
}

/** Looks for a new version now (Settings). */
export async function checkForUpdate(): Promise<void> {
  await registration?.update().catch(() => undefined);
}

/**
 * The person chose to update: every guard must agree (work saved, nothing half drawn),
 * then the waiting version takes over and the page reloads into it.
 */
export async function applyUpdate(): Promise<void> {
  const env = environment;
  if (!env || useOfflineShell.getState().applying) return;
  set({ applying: true, problem: null });
  for (const guard of [...guards]) {
    let result: UpdateGuardResult;
    try {
      result = await guard();
    } catch {
      result = { ok: false, reason: 'Your work could not be checked, so the update waits.' };
    }
    if (!result.ok) {
      set({ applying: false, problem: result.reason });
      return;
    }
  }
  userChoseUpdate = true;
  const waiting = registration?.waiting;
  if (waiting) {
    waiting.postMessage({ type: 'SKIP_WAITING' });
    // The reload happens when the new version takes control; if it never does (it was
    // replaced meanwhile), the work is saved, so reloading is still safe.
    (env.setTimeout ?? ((fn, ms) => window.setTimeout(fn, ms)))(() => env.reload(), 10_000);
  } else {
    env.reload(); // another tab already switched versions; this tab only needs to reload
  }
}

export function dismissUpdate(): void {
  set({ dismissed: true, problem: null });
}

/** Resets module state between tests. */
export function resetOfflineShellForTesting(): void {
  guards.clear();
  registration = null;
  environment = null;
  userChoseUpdate = false;
  useOfflineShell.setState({
    status: 'disabled',
    detail: null,
    updateWaiting: false,
    updatedElsewhere: false,
    applying: false,
    problem: null,
    dismissed: false,
  });
}

/** The real browser, for main.tsx. */
export function browserShellEnvironment(): ShellEnvironment {
  const base = import.meta.env.BASE_URL;
  return {
    enabled: import.meta.env.PROD,
    container:
      typeof navigator !== 'undefined' && 'serviceWorker' in navigator
        ? navigator.serviceWorker
        : undefined,
    secure: typeof window !== 'undefined' && window.isSecureContext,
    scriptUrl: `${base}sw.js`,
    scope: base,
    reload: () => window.location.reload(),
    scheduleChecks: (check) => {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });
      window.setInterval(check, 60 * 60 * 1000);
    },
  };
}
